import { FC, useCallback, useContext } from 'react';
import styled from 'styled-components';
import { Registration } from '@stax-core/types-omni';
import { CenteredContent } from '../components/CenteredContent';
import { coreapi } from '../../api';
import {
  AuthStore,
  BrandMCC,
  BrandStore,
  RegistrationStore,
  updateRegistration,
} from '../../context';
import {
  useToaster,
  useWriteableFieldFormValidation,
} from '../../hooks';
import Autocomplete from '../../components/Autocomplete/Autocomplete';
import { OptionItem } from '../../components/Autocomplete';
import { BackButton } from '../components/BackButton';
import { NextButton } from '../components/NextButton';
import { Field } from '../../components';
import { MainForm } from '../components/application/MainForm';

interface FieldValues extends Pick<Registration, 'service_you_provide'> {}

const StyledField = styled.div`
  margin-bottom: 10px;
`;

export const IndustryCategoryPage: FC = () => {
  const { state: { mcc } } = useContext(BrandStore);
  const { toaster, toast } = useToaster();
  const { state: { auth } } = useContext(AuthStore);
  const {
    dispatch,
    state: { registration },
  } = useContext(RegistrationStore);
  const authToken = auth?.token ?? null;

  const validate = useWriteableFieldFormValidation((values: FieldValues) => {
    const errors: Partial<Record<keyof FieldValues, any>> = {};
    if (!values?.service_you_provide)
      errors['service_you_provide'] = 'Please provide service_you_provide';
    return errors;
  });

  const onSubmit = useCallback(
    async (formValues: FieldValues) => {
      const payload = {
        service_you_provide: formValues?.service_you_provide,
      };

      try {
        const r = await coreapi.put(authToken!, '/team/registration', payload);
        dispatch(updateRegistration(r));
        return true;
      } catch (error) {
        toaster(toast.error(error, 'There was a problem saving your changes.'));
      }
    },
    [authToken, dispatch, toast, toaster],
  );

  return (
    <MainForm<FieldValues>
      initialValues={mapInitialFormValues(registration)}
      validate={validate}
      onSubmit={onSubmit}
    >
      {formProps => (
        <CenteredContent
          id="industry-category"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 100 }}
          exit={{ opacity: 0, y: -20 }}
        >
          <BackButton id="industry-category-back" />
          <h2>Industry category</h2>
          <div>
            <p>Select the category that best fits your business.</p>
            <StyledField>
              <Field
                name={`service_you_provide`}
                label={'Search Industry'}
                type="text"
                value={formProps.values.service_you_provide!}
              >
                {props => (
                  <Autocomplete
                    inputProps={{
                      ...props.input,
                      icon: ['fas', 'search'],
                    }}
                    formatter={value =>
                      value?.includes(' - ') ? value?.split(' - ')[1] : value
                    }
                    onChange={props.input.onChange}
                    onSelect={value => {
                      props.input.onChange(value.label);
                      formProps.form.change('service_you_provide', value.label);
                    }}
                    options={(mcc || []).reduce((acc, curr) => {
                      if (curr.name && curr.displayName) {
                        const value = String(
                          curr.displayName?.split(`${curr.name} -`)[1],
                        ).trim();
                        if (value && !value.toLowerCase().includes('n/a')) {
                          acc.push({
                            ...curr,
                            label: value,
                            key: curr.displayName,
                          });
                        }
                      }
                      return acc;
                    }, [] as (BrandMCC & OptionItem)[])}
                  />
                )}
              </Field>
            </StyledField>
          </div>
          <NextButton id="industry-category-continue" formProps={formProps} />
        </CenteredContent>
      )}
    </MainForm>
  );
};

function mapInitialFormValues(
  registration: Registration | null,
): Partial<FieldValues> {
  return registration
    ? {
        service_you_provide: registration.service_you_provide,
      }
    : ({ service_you_provide: null } as FieldValues);
}
